// Выбор дня и времени. Сетка времени — плотная, табличные цифры; занятое
// время видно, но недоступно для выбора.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/Stack';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { publicApi } from '@/lib/api/public';
import type { Slot } from '@/lib/api/schemas';
import { ErrorView } from '@/components/StateViews';
import { dateLabel, todayIn } from '@/lib/time';
import { cn } from '@/lib/utils';

const DAYS = 14;

export function useSlots(slug: string, serviceId: string | null, tz: string) {
  const from = todayIn(tz);
  return useQuery({
    queryKey: ['slots', slug, serviceId, from],
    queryFn: () => publicApi.slots(slug, serviceId as string, from, DAYS),
    enabled: !!serviceId,
    staleTime: 20_000,
    refetchInterval: 60_000,
  });
}

export function SlotPicker({
  slug,
  tz,
  serviceId,
  value,
  onChange,
  exclude,
}: {
  slug: string;
  tz: string;
  serviceId: string;
  value: string | null;
  onChange: (startsAt: string) => void;
  /** Текущее время записи при переносе — показывается отмеченным */
  exclude?: string;
}) {
  const q = useSlots(slug, serviceId, tz);
  const byDay = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of q.data ?? []) m.set(s.day, [...(m.get(s.day) ?? []), s]);
    return m;
  }, [q.data]);
  const days = [...byDay.keys()];
  const valueDay = value ? q.data?.find((s) => s.starts_at === value || Date.parse(s.starts_at) === Date.parse(value))?.day : undefined;
  const firstAvailable = days.find((d) => byDay.get(d)?.some((s) => s.available));
  const [day, setDay] = useState<string | undefined>(valueDay);
  useEffect(() => {
    if (!day && (valueDay ?? firstAvailable)) setDay(valueDay ?? firstAvailable);
  }, [day, valueDay, firstAvailable]);

  if (q.isPending) {
    return (
      <VStack gap={3} aria-busy="true">
        <Skeleton height={64} radius="rounded" />
        <Skeleton height={160} radius="rounded" index={1} />
      </VStack>
    );
  }
  if (q.isError) return <ErrorView error={q.error} onRetry={() => void q.refetch()} title="Не удалось загрузить время" />;
  if (!firstAvailable) {
    return (
      <VStack gap={2} paddingBlock={6}>
        <Text weight="semibold">Свободного времени на ближайшие две недели нет</Text>
        <Text color="secondary">Позвоните в студию: возможно, получится найти окно вручную.</Text>
      </VStack>
    );
  }

  const selected = day ? (byDay.get(day) ?? []) : [];

  return (
    <VStack gap={4}>
      <ul className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1" aria-label="День">
        {days.map((d) => {
          const free = byDay.get(d)?.filter((s) => s.available).length ?? 0;
          const active = d === day;
          return (
            <li key={d} className="snap-start">
              <button
                type="button"
                aria-pressed={active}
                disabled={free === 0}
                onClick={() => setDay(d)}
                className={cn(
                  'flex min-h-16 w-16 flex-col items-center justify-center rounded-lg border text-sm transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-bg',
                  active ? 'border-accent-bg bg-accent-bg text-on-accent' : 'border-border bg-body text-primary',
                  free === 0 && 'opacity-40',
                )}
                aria-label={`${dateLabel(d, { weekday: 'long', day: 'numeric', month: 'long' })}${free ? `, свободно ${free}` : ', нет свободного времени'}`}
              >
                <span className={cn('text-xs', active ? 'text-on-accent' : 'text-secondary')}>
                  {dateLabel(d, { weekday: 'short' })}
                </span>
                <span className="tabular text-lg font-semibold">{dateLabel(d, { day: 'numeric' })}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {day ? (
        <VStack gap={2}>
          <Text color="secondary">{dateLabel(day, { weekday: 'long', day: 'numeric', month: 'long' })}</Text>
          <ul className="grid grid-cols-4 gap-2" aria-label="Время приезда">
            {selected.map((s) => {
              const active = value !== null && Date.parse(s.starts_at) === Date.parse(value);
              const current = exclude !== undefined && Date.parse(s.starts_at) === Date.parse(exclude);
              return (
                <li key={s.starts_at}>
                  <button
                    type="button"
                    disabled={!s.available && !current}
                    aria-pressed={active}
                    aria-label={`${s.time}${current ? ', текущее время записи' : s.available ? '' : ', занято'}`}
                    onClick={() => onChange(s.starts_at)}
                    className={cn(
                      'tabular flex min-h-12 w-full items-center justify-center rounded-md border text-base font-medium',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-bg',
                      active
                        ? 'border-accent-bg bg-accent-bg text-on-accent'
                        : s.available
                          ? 'border-border bg-body text-primary'
                          : 'border-transparent bg-transparent text-disabled line-through',
                      current && !active && 'border-dashed border-accent-bg',
                    )}
                  >
                    {s.time}
                  </button>
                </li>
              );
            })}
          </ul>
        </VStack>
      ) : null}
    </VStack>
  );
}
